// TikTok platform admin endpoints — the Business Center list, the multi-token
// SLOT registry, and the OAuth kick-off. Consumed verbatim; no invented fields.
//
//   GET  /admin/tiktok/bcs        — { bcs: [{ bc_id, name }] } ("bcs" always [],
//                                   never null; name may be "" — render bc_id
//                                   then). Sanitized errors: 503 = master-token
//                                   client not wired, 502 = live TikTok call
//                                   failed. BOTH mean "fall back to manual
//                                   bc_id input", as does a 404 (endpoint not
//                                   deployed yet).
//   GET  /admin/tiktok/slots      — { slots: [TokenSlot] } — slots 1..8, ALWAYS
//                                   all present. Slot 1 is the primary.
//   PUT  /admin/tiktok/slots/{n}  — { label?, status? } -> the updated slot. Both
//                                   fields are optional POINTERS server-side, so
//                                   an omitted one leaves that value untouched.
//                                   400 slot_invalid (outside 1..8),
//                                   400 status_invalid (not active|disabled),
//                                   409 primary_slot (disabling slot 1),
//                                   409 slot_empty (activating a never-authorized
//                                   slot).
//                                   CAVEAT: the backend builds THIS response
//                                   without the advertiser count, so its
//                                   `advertiser_count` is always 0 — it is not a
//                                   real count and must never overwrite one.
//   GET  /admin/tiktok/slots/{n}/advertisers
//                                 — { slot, advertisers: [SlotAdvertiser] } — the
//                                   slot's STORED authorized advertiser set. This
//                                   is a plain database read: it does NOT re-query
//                                   TikTok with the slot's token. 400 slot_invalid.
//   GET  /admin/tiktok/oauth/start?slot=N
//                                 — { authorize_url } — the TikTok consent page
//                                   the operator opens to mint/re-mint THAT
//                                   slot's token (the slot rides in the signed,
//                                   single-use, 10-min state). The code exchange
//                                   + Secret Manager write happen ENTIRELY
//                                   server-side; the browser only ever sees the
//                                   consent URL — never a token.
//                                   400 slot_invalid, 503 slot_not_wired (that
//                                   slot has no Secret Manager writer).
//   GET  /admin/tiktok/coverage   — { accounts: [CoverageAccount], summary:
//                                   { total, covered, uncovered, balance_readable },
//                                   denied_bcs: [DeniedBC] } — EVERY cached
//                                   BC account with the ACTIVE slots authorized
//                                   for it, not just the uncovered ones. Since the
//                                   finance-visibility batch each account also
//                                   carries balance_readable + balance_slots (the
//                                   slots that have actually completed a finance
//                                   read for it), and denied_bcs lists the Business
//                                   Centers that answered 40002 "no finance" to a
//                                   slot — the previously SILENT failure mode.
//
// ERROR ENVELOPE: every route here refuses through the backend's writeError,
// which renders {"error": "<code>"} — the sanitized reason code arrives under
// `error`, NOT under `reason`. (Other admin surfaces use a richer
// {error, reason} body; this one does not. Do not read `reason` here.)
//
// SECURITY: no token value is ever returned by any of these routes, and this
// module never asks for one. `last_error` is a SANITIZED reason code / short
// string produced server-side (invariants 1 and 3).

import { api, type ApiError } from "./client";

export interface BCOption {
  bc_id: string;
  // May be "" when upstream omits it — display bc_id then.
  name: string;
}

// List the Business Centers visible to the master token. Returns null when the
// endpoint is unavailable (404 not deployed / 503 not wired / 502 upstream
// error) — callers fall back to a manual bc_id input. Throws only for
// genuinely unexpected failures.
export async function listBCs(): Promise<BCOption[] | null> {
  try {
    const res = await api.get<{ bcs: BCOption[] }>("/admin/tiktok/bcs");
    return res.bcs ?? [];
  } catch (err) {
    const status = (err as ApiError)?.status;
    if (status === 404 || status === 502 || status === 503) return null;
    throw err;
  }
}

// ---- Multi-token slots -------------------------------------------------------
//
// TikTok grants FINANCE access per TikTok USER, so one authorized identity
// often cannot read every account's balance. Each slot holds one authorized
// TikTok identity's server-held token; slot 1 is the original primary.

// The primary slot — cannot be disabled (the backend answers 409 primary_slot).
export const PRIMARY_SLOT = 1;

// How many slots the backend exposes. Informational only: we always RENDER the
// slots the backend returned, never a locally synthesized 1..8 range.
export const MAX_SLOTS = 8;

//   active   — the slot holds a token and is used for reads.
//   disabled — the slot holds a token but the operator turned it off.
//   empty    — no token has ever been authorized into this slot.
export type TokenSlotStatus = "active" | "disabled" | "empty";

export interface TokenSlot {
  // 1..8. Slot 1 is the primary.
  slot: number;
  // Free operator text; "" by default (key always present).
  label: string;
  status: TokenSlotStatus;
  // RFC3339 UTC STRING, "" when unset — never a number. Render via new Date(s).
  authorized_at: string;
  // Advertisers this slot's token is authorized for (0 when empty).
  advertiser_count: number;
  // RFC3339 UTC STRING of the last successful upstream use, "" when unset.
  last_ok_at: string;
  // SANITIZED reason code / short string, "" when none. NEVER a token or a raw
  // upstream body — displayed as-is.
  last_error: string;
}

export interface TokenSlotList {
  slots: TokenSlot[];
}

// Fields the operator may change on a slot. Both optional; send only what
// changed.
export interface TokenSlotPatch {
  label?: string;
  status?: "active" | "disabled";
}

// One (slot, advertiser) authorization row.
export interface SlotAdvertiser {
  advertiser_id: string;
  // "auth"  — captured when the slot was authorized;
  // "probe" — learned because a live call with this slot's token succeeded.
  source: string;
  // RFC3339 UTC STRINGs; last_ok_at is "" when the pair has never been used.
  first_seen_at: string;
  last_ok_at: string;
}

// One cached BC account and the ACTIVE slots that can reach it. The coverage
// endpoint reports EVERY account, covered or not — `covered` is the flag.
export interface CoverageAccount {
  advertiser_id: string;
  // May be "" — render advertiser_id then.
  name: string;
  bc_id: string;
  // The ACTIVE slot numbers authorized for this account. Never null (the
  // backend sends [] rather than omitting it).
  slots: number[];
  // Server-computed: slots.length > 0. "Reachable" — the account can be READ,
  // which is NOT the same as its BALANCE being readable (finance is a separate
  // TikTok grant).
  covered: boolean;
  // TRUE when at least one slot has actually completed a FINANCE read for this
  // advertiser. OPTIONAL on purpose: an older backend omits it entirely, and
  // defaulting a missing field to false would flag every account as
  // finance-blind. `undefined` = "this backend does not report it".
  balance_readable?: boolean;
  // Slots with a recorded finance-read success for this advertiser ([] when
  // none; absent on an older backend).
  balance_slots?: number[];
}

// A Business Center that refused a finance read (TikTok 40002 "no finance
// permission") to at least one slot — i.e. the authorized identity is in the
// BC but has no finance role there, so every account it owns shows a blank
// balance. This used to fail silently.
export interface DeniedBC {
  bc_id: string;
  // Slot numbers that got the refusal.
  slots: number[];
  // RFC3339 UTC string of the most recent refusal ("" when unset).
  last_seen: string;
}

export interface CoverageSummary {
  // BC accounts known to the dashboard cache.
  total: number;
  // Of those, how many at least one ACTIVE slot is authorized for.
  covered: number;
  uncovered: number;
  // Of those, how many have a working FINANCE read. Optional for the same
  // reason as CoverageAccount.balance_readable (older backend omits it).
  balance_readable?: number;
}

export interface TokenCoverage {
  accounts: CoverageAccount[];
  summary: CoverageSummary;
  // Always an array after normalization ([] when the backend sends none or is
  // an older build).
  denied_bcs: DeniedBC[];
}

// Does THIS backend report finance readability at all? Answered from the data,
// never assumed: the summary counter or any account carrying the flag proves
// the field exists. When false the card keeps the old covered-only rendering
// rather than inventing a scary "0 finance-readable".
export function reportsBalanceReadability(coverage: TokenCoverage): boolean {
  return (
    coverage.summary.balance_readable !== undefined ||
    coverage.accounts.some((a) => a.balance_readable !== undefined)
  );
}

// All token slots (1..8, always all present). Returns the array as the backend
// ordered it; `?? []` only guards a null body.
export async function listTokenSlots(): Promise<TokenSlot[]> {
  const res = await api.get<TokenSlotList>("/admin/tiktok/slots");
  return res.slots ?? [];
}

// Update a slot's label and/or operator on/off status. Resolves with the
// backend's updated slot object (the authoritative post-write state) — EXCEPT
// its advertiser_count, which this endpoint does not compute and always reports
// as 0. Callers merging this into a known row must keep their existing count.
// Rejects 409 {error:"primary_slot"} when disabling slot 1 and
// 409 {error:"slot_empty"} when activating a slot with no token.
export function updateTokenSlot(
  slot: number,
  patch: TokenSlotPatch,
): Promise<TokenSlot> {
  return api.put<TokenSlot>(`/admin/tiktok/slots/${slot}`, patch);
}

// Kick off the TikTok consent flow for ONE slot: the backend builds the
// authorize URL and signs the slot into the single-use state (TTL ~10 min).
// The caller window.open()s it; the token never comes back to the browser.
export function startSlotAuth(
  slot: number = PRIMARY_SLOT,
): Promise<{ authorize_url: string }> {
  return api.get<{ authorize_url: string }>(
    `/admin/tiktok/oauth/start?slot=${encodeURIComponent(String(slot))}`,
  );
}

// Read a slot's STORED authorized advertiser set. Note what this is NOT: it
// does not call TikTok, so it cannot discover a grant the backend has not
// already recorded (those arrive via the consent flow or a successful probe).
// It answers "what is this slot authorized for right now", nothing more.
export async function listSlotAdvertisers(
  slot: number,
): Promise<{ slot: number; advertisers: SlotAdvertiser[] }> {
  const res = await api.get<{ slot: number; advertisers: SlotAdvertiser[] }>(
    `/admin/tiktok/slots/${slot}/advertisers`,
  );
  return { slot: res.slot ?? slot, advertisers: res.advertisers ?? [] };
}

// Every cached BC account with the ACTIVE slots authorized for it, plus the
// server's own totals. Read-only. The `?? ` guards only a malformed/absent
// body; the summary is re-derived rather than zeroed so the counts can never
// contradict the list rendered beside them.
export async function getTokenCoverage(): Promise<TokenCoverage> {
  const res = await api.get<Partial<TokenCoverage>>("/admin/tiktok/coverage");
  const accounts = res.accounts ?? [];
  const summary = res.summary ?? {
    total: accounts.length,
    covered: accounts.filter((a) => a.covered).length,
    uncovered: accounts.filter((a) => !a.covered).length,
  };
  // balance_readable is only DERIVED when the accounts prove the backend
  // reports it; if nothing carries the flag it stays undefined so the UI can
  // tell "no finance data" apart from "zero finance-readable accounts".
  if (
    summary.balance_readable === undefined &&
    accounts.some((a) => a.balance_readable !== undefined)
  ) {
    summary.balance_readable = accounts.filter(
      (a) => a.balance_readable === true,
    ).length;
  }
  return {
    accounts,
    summary,
    denied_bcs: res.denied_bcs ?? [],
  };
}

// Back-compat alias for the original single-token re-mint: slot 1 IS the
// former master token.
export function startMasterTokenRemint(): Promise<{ authorize_url: string }> {
  return startSlotAuth(PRIMARY_SLOT);
}
